import React, { useCallback, useMemo, useState } from 'react';
import {
    SavedAIDesign,
    TreeCutSubScenario,
    useOneButtonStore,
    type SelectedDesignForPricing,
} from '../../../../store/oneButtonStore';
import { HeartIcon } from '@phosphor-icons/react';
import { useViewerStore } from '../../../../store/store';
import { saveWishlist } from '../../../../services/geminiService';
import {
    buildDashboardLayoutPerfDetailLine,
    DashboardAiCatalogListRow,
    formatDashboardAnnualProductionLine,
    type DashboardAiCatalogListRowStatusVariant,
} from './sections/DashboardAiCatalogListRow';
import { computeSystemKwFromModules } from './hooks/useDashboardDesignHandlers';
import { DashboardAiDesignsEpcModal } from './sections/DashboardAiDesignsEpcModal';
import type { DashboardAiDesignEpcRow } from './sections/DashboardAiDesignsEpcModal.types';
import type { DesignEpcCell, EpcDisplayPartner } from './utils/pricing';
import { STABLE_EMPTY_OBJECT } from './utils/stableEmpty';

/** Non-empty trimmed string or undefined (empty URL must not be passed to <img>). */
function nonEmptyUrl(u: string | null | undefined): string | undefined {
    if (typeof u !== 'string') return undefined;
    const t = u.trim();
    return t.length > 0 ? t : undefined;
}

/** Snapshot URL from a tree-cutting scenario row (modal / AI module filling). */
export function treeCutScenarioSnapshotFromRow(scenario: any): string | undefined {
    const u = scenario?.snapshotUrl ?? scenario?.layoutSnapshotUrl;
    return nonEmptyUrl(u);
}

/** Resolve tree-cut thumbnail for a wishlisted sub-scenario. */
function resolveWishlistTreeCutSnapshot(
    design: SavedAIDesign,
    sub: TreeCutSubScenario,
    treeScenariosByLayoutKey?: Record<string, any[]>,
): string | undefined {
    const persisted = nonEmptyUrl(sub.snapshotUrl);
    if (persisted) return persisted;

    const scenarios =
        treeScenariosByLayoutKey?.[design.layoutKey] ?? design.treeScenarios ?? [];
    const scenario = scenarios[sub.scenarioIndex];
    return treeCutScenarioSnapshotFromRow(scenario);
}

/**
 * Same thumbnail resolution order as {@link DashboardAiLayoutCardView} AI Design cards, plus
 * persisted `design.snapshotUrl` when live viewer maps no longer have the key (e.g. wishlist
 * restored from server without embedded images).
 *
 * Tree-cutting wishlist rows prefer the scenario's own snapshot and never fall back to the
 * parent layout/category thumbnail.
 */
export function resolveWishlistedDesignSnapshotUrl(
    design: SavedAIDesign,
    layoutSnapshotsByKey: Record<string, string | undefined>,
    aiCategoryLayoutVariants: Record<string, unknown[]>,
    precomputedFallback?: string | undefined,
    sub?: TreeCutSubScenario,
    treeScenariosByLayoutKey?: Record<string, any[]>,
): string | undefined {
    if (sub && isWishlistTreeCutScenario(sub)) {
        const treeSnap = resolveWishlistTreeCutSnapshot(design, sub, treeScenariosByLayoutKey);
        if (treeSnap) return treeSnap;
        return undefined;
    }

    const key = design.layoutKey;
    const categoryLabel = design.categoryId?.trim() || undefined;

    const thumbDirect = nonEmptyUrl(layoutSnapshotsByKey[key]);

    const thumbFromCategory =
        categoryLabel &&
        nonEmptyUrl(
            (
                (((aiCategoryLayoutVariants as Record<string, unknown[]>)?.[categoryLabel] ?? []) as any[]).find(
                    (v: any) => typeof v?.snapshotUrl === 'string' && v.snapshotUrl.trim().length > 0,
                )?.snapshotUrl as string | undefined
            ),
        );

    let variantSnapshot: string | undefined;
    if (key.startsWith('variant:') && categoryLabel) {
        const prefix = `variant:${categoryLabel}:`;
        const baseKey = key.startsWith(prefix) ? key.slice(prefix.length) : key.split(':').slice(2).join(':');
        if (baseKey) {
            const arr = ((aiCategoryLayoutVariants as Record<string, unknown[]>)?.[categoryLabel] ?? []) as any[];
            const match = arr.find((v: any) => v?.id === baseKey || v?.label === baseKey);
            variantSnapshot = nonEmptyUrl(match?.snapshotUrl);
        }
    }

    const fallback =
        precomputedFallback ??
        (() => {
            const directDefault = nonEmptyUrl((layoutSnapshotsByKey as any)?.default) ?? nonEmptyUrl((layoutSnapshotsByKey as any)?.defaultInitial);
            if (directDefault) return directDefault;
            const firstLayout = Object.values(layoutSnapshotsByKey || {}).find(
                (v) => typeof v === 'string' && v.trim().length > 0,
            ) as string | undefined;
            if (firstLayout) return nonEmptyUrl(firstLayout);
            for (const variants of Object.values(aiCategoryLayoutVariants || {})) {
                const arr = Array.isArray(variants) ? variants : [];
                for (const variant of arr as any[]) {
                    const snap = nonEmptyUrl(variant?.snapshotUrl);
                    if (snap) return snap;
                }
            }
            return undefined;
        })();

    const persisted = nonEmptyUrl(design.snapshotUrl);

    return thumbDirect || thumbFromCategory || variantSnapshot || fallback || persisted;
}

/** Whether this wishlisted row is a tree-cutting variant (not a plain layout heart). */
export function isWishlistTreeCutScenario(sub: TreeCutSubScenario): boolean {
    if (sub.treeIds?.length) return true;
    if (sub.treeLabel && sub.treeLabel !== 'Layout') return true;
    return sub.scenarioIndex > 0;
}

function normalizeTreeCutToken(value: string): string {
    return value
        .replace(/^cut\s+trees?\s+/i, '')
        .replace(/^tree\s+/i, '')
        .trim();
}

/** Primary card name: AI design category when set, otherwise layout label. */
export function wishlistDesignBaseName(design: SavedAIDesign): string {
    const category = design.categoryId?.trim();
    if (category) return category;
    return (design.label || 'AI design').trim();
}

/** Tree-cutting detail for titles, e.g. "A, B" or "Ideal (no cuts)". */
export function wishlistTreeCutDetail(sub: TreeCutSubScenario): string | null {
    if (!isWishlistTreeCutScenario(sub)) return null;

    if (sub.treeLabel === 'Ideal (no cuts)' || (sub.scenarioIndex === 0 && !sub.treeIds?.length)) {
        return 'Ideal (no cuts)';
    }

    if (sub.treeLabel && sub.treeLabel !== 'Layout') {
        const normalized = normalizeTreeCutToken(sub.treeLabel);
        if (normalized) return normalized;
    }

    if (sub.treeIds?.length) {
        return sub.treeIds.map((id) => normalizeTreeCutToken(id) || id).join(', ');
    }

    if (sub.scenarioIndex > 0) {
        return `Scenario ${sub.scenarioIndex + 1}`;
    }

    return null;
}

/** Per-scenario short tag for table summaries. */
export function wishlistScenarioTag(sub: TreeCutSubScenario, _design: SavedAIDesign): string {
    const treeCut = wishlistTreeCutDetail(sub);
    if (!treeCut) return 'Layout';
    if (treeCut === 'Ideal (no cuts)') return treeCut;
    return `Tree Cut ${treeCut}`;
}

/** Card title, e.g. "Best Solar Access - Tree Cut A, B". */
export function wishlistRowTitle(design: SavedAIDesign, sub: TreeCutSubScenario): string {
    const base = wishlistDesignBaseName(design);
    const treeCut = wishlistTreeCutDetail(sub);
    if (!treeCut) return base;
    if (treeCut === 'Ideal (no cuts)') return `${base} - Ideal (no cuts)`;
    return `${base} - Tree Cut ${treeCut}`;
}

export function wishlistTreeCount(sub: TreeCutSubScenario, design: SavedAIDesign): number {
    if (sub.treeIds?.length) return sub.treeIds.length;
    return design.treeCount ?? 0;
}

/** Stable per wishlisted scenario — used for EPC modal rows and pricing cache keys. */
export function wishlistScenarioCardKey(design: SavedAIDesign, sub: TreeCutSubScenario): string {
    return `wishlist-${design.aiDesignId}-${sub.id}`;
}

function wishlistScenarioProductionKwh(sub: TreeCutSubScenario, design: SavedAIDesign): number | undefined {
    if (typeof sub.productionW === 'number' && Number.isFinite(sub.productionW) && sub.productionW > 0) {
        return sub.productionW;
    }
    if (typeof design.productionW === 'number' && Number.isFinite(design.productionW) && design.productionW > 0) {
        return design.productionW;
    }
    return undefined;
}

function wishlistScenarioSystemKw(
    sub: TreeCutSubScenario,
    design: SavedAIDesign,
    modulePowerW: number,
): string {
    const modules =
        typeof sub.modules === 'number' && Number.isFinite(sub.modules) && sub.modules > 0
            ? sub.modules
            : typeof design.modules === 'number' && Number.isFinite(design.modules)
              ? design.modules
              : 0;
    const fromModules = computeSystemKwFromModules(modules, modulePowerW);
    if (fromModules > 0) return String(fromModules);
    const kw =
        typeof sub.systemKw === 'number' && Number.isFinite(sub.systemKw)
            ? sub.systemKw
            : typeof design.systemKw === 'number' && Number.isFinite(design.systemKw)
              ? design.systemKw
              : 0;
    return String(kw);
}

interface WishListSectionProps {
    setSelectedRoofCombinationKey: (roofCombinationKey: string) => void;
    handleApplyLayoutClick?: (layoutKey: string, categoryLabel?: string) => void | Promise<void>;
    handleUseThisLayout: (
        scenarioIndex: number,
        layoutKey: string,
        treeScenarios?: any[],
        categoryLabel?: string,
    ) => void;
    handlePricingForSavedDesign: (design: SavedAIDesign) => void;
    selectedDesignForPricing: SelectedDesignForPricing | null;
    pricingDesignForTrigger: SelectedDesignForPricing | null;
    pricingSelectionLoading: boolean;
    solarUtilityData: unknown;
    ensureMainCardPricingForCard: (
        cardKey: string,
        estProd: number | undefined,
        systemKw: string,
    ) => Promise<unknown | null>;
    getDesignEpcByPartner: (
        cardKey: string,
        estProd?: number,
        systemKw?: string,
    ) => Record<EpcDisplayPartner, DesignEpcCell>;
    getMainCardPartnerCellState: (
        cardKey: string,
        partner: EpcDisplayPartner,
        estProd?: number,
        systemKw?: string,
    ) => { status?: string; errorDetail?: string } | undefined;
    isMainCardEpcLoading: (cardKey: string, estProd?: number, systemKw?: string) => boolean;
    /** Legacy, kept for compatibility. Wishlist now always renders card view. */
    catalogViewMode: 'table' | 'card';
    setCatalogViewMode: (mode: 'table' | 'card') => void;
    /** Hide title, View toggle, and section subtitle (e.g. 3D viewport / compact layouts). */
    hideHeaderChrome?: boolean;
}

function WishListSection(props: WishListSectionProps) {
    const {
        setSelectedRoofCombinationKey,
        handleApplyLayoutClick,
        handleUseThisLayout,
        handlePricingForSavedDesign,
        selectedDesignForPricing,
        pricingDesignForTrigger,
        pricingSelectionLoading,
        solarUtilityData,
        ensureMainCardPricingForCard,
        getDesignEpcByPartner,
        getMainCardPartnerCellState,
        isMainCardEpcLoading,
        catalogViewMode: _catalogViewMode,
        setCatalogViewMode: _setCatalogViewMode,
        hideHeaderChrome = false,
    } = props;

    const setIsDashboardOpen = (value: boolean) => useViewerStore.setState({ isDashboardOpen: value });
    const isDashboardOpen = useViewerStore((s) => s.isDashboardOpen);
    const activeAIDesignId = useOneButtonStore((s) => s.activeAIDesignId);
    const setActiveAIDesignId = useOneButtonStore((s) => s.setActiveAIDesignId);
    const activeSubScenarioId = useOneButtonStore((s) => s.activeSubScenarioId);
    const setActiveSubScenarioId = useOneButtonStore((s) => s.setActiveSubScenarioId);
    const savedAIDesigns = useOneButtonStore((s) => s.savedAIDesigns);
    const removeTreeCutSubScenario = useOneButtonStore((s) => s.removeTreeCutSubScenario);
    const theme = useOneButtonStore((s) => s.theme);
    /** Per-layout simulation metrics — same source the AI Designs card view reads from. */
    const layoutMetricsByKey = useViewerStore(
        (s: any) =>
            (s.layoutMetricsByKey ?? STABLE_EMPTY_OBJECT) as Record<
                string,
                {
                    efficiency?: number | null;
                    solarAccess?: number | null;
                    multiplier?: number | null;
                } | undefined
            >,
    );
    const layoutSnapshotsByKey = useViewerStore(
        (s: any) => (s.layoutSnapshotsByKey ?? STABLE_EMPTY_OBJECT) as Record<string, string | undefined>,
    );
    const aiCategoryLayoutVariants = useViewerStore(
        (s: any) => (s.aiCategoryLayoutVariants ?? STABLE_EMPTY_OBJECT) as Record<string, unknown[]>,
    );
    const treeScenariosByLayoutKey = useViewerStore(
        (s: any) =>
            (s.aiModuleFillingTreeScenariosByLayoutKey ?? STABLE_EMPTY_OBJECT) as Record<string, any[]>,
    );
    const isLoadingSnapshots = useViewerStore((s: any) => Boolean((s as any).isLoadingSnapshots));
    const activeModule = useViewerStore((s) => s.activeModule);
    const modulePowerW = (activeModule as { power?: number })?.power ?? 0;

    const removeFromWishlist = useCallback(
        (designId: string, subId: string) => {
            removeTreeCutSubScenario(designId, subId, (newWishlist) => {
                void saveWishlist(newWishlist);
            });
        },
        [removeTreeCutSubScenario],
    );

    const openWishlistRowInViewer = useCallback(
        (design: SavedAIDesign, sub: TreeCutSubScenario) => {
            useViewerStore.setState({ isAiGeneratedDesign: true });
            setActiveAIDesignId(design.aiDesignId);
            setActiveSubScenarioId(sub.id);
            const selectionKey =
                design.layoutKey === 'default' ? 'default' : design.categoryId?.trim() || design.layoutKey;
            setSelectedRoofCombinationKey(selectionKey);
            setIsDashboardOpen(false);

            const applyLayoutKey = design.layoutKey === 'default' ? 'default' : design.layoutKey;
            const applyCategoryLabel =
                design.layoutKey === 'default' ? undefined : design.categoryId?.trim() || undefined;

            if (!isWishlistTreeCutScenario(sub) && typeof handleApplyLayoutClick === 'function') {
                void handleApplyLayoutClick(applyLayoutKey, applyCategoryLabel, {
                    syncCatalogPanelSelection: false,
                });
                return;
            }

            if (typeof handleUseThisLayout === 'function') {
                void handleUseThisLayout(
                    sub.scenarioIndex,
                    design.layoutKey,
                    design.treeScenarios,
                    design.categoryId,
                );
            }
        },
        [
            handleApplyLayoutClick,
            handleUseThisLayout,
            setActiveAIDesignId,
            setActiveSubScenarioId,
            setSelectedRoofCombinationKey,
            setIsDashboardOpen,
        ],
    );

    const scenarioCards = useMemo(
        () =>
            savedAIDesigns.flatMap((design) =>
                (design.treeCutSubScenarios ?? []).map((sub) => ({ design, sub })),
            ),
        [savedAIDesigns],
    );

    const hasAi = scenarioCards.length > 0;
    const fallbackSnapshotUrl = useMemo(() => {
        const directDefault =
            nonEmptyUrl((layoutSnapshotsByKey as any)?.default) ?? nonEmptyUrl((layoutSnapshotsByKey as any)?.defaultInitial);
        if (directDefault) return directDefault;
        const firstLayoutSnapshot = Object.values(layoutSnapshotsByKey || {}).find(
            (v) => typeof v === 'string' && v.trim().length > 0,
        ) as string | undefined;
        if (firstLayoutSnapshot) return nonEmptyUrl(firstLayoutSnapshot);
        for (const variants of Object.values(aiCategoryLayoutVariants || {})) {
            const arr = Array.isArray(variants) ? variants : [];
            for (const variant of arr as any[]) {
                const snap = nonEmptyUrl(variant?.snapshotUrl);
                if (snap) return snap;
            }
        }
        return undefined;
    }, [layoutSnapshotsByKey, aiCategoryLayoutVariants]);

    const [epcModalOpen, setEpcModalOpen] = useState(false);

    const handleOpenEpcModal = useCallback(() => {
        setEpcModalOpen(true);
    }, []);

    const handleCloseEpcModal = useCallback(() => {
        setEpcModalOpen(false);
    }, []);

    const epcModalDesigns = useMemo((): DashboardAiDesignEpcRow[] => {
        const rows: DashboardAiDesignEpcRow[] = [];
        for (const { design, sub } of scenarioCards) {
            const cardKey = wishlistScenarioCardKey(design, sub);
            const estProd = wishlistScenarioProductionKwh(sub, design);
            const systemKw = wishlistScenarioSystemKw(sub, design, modulePowerW);
            if (estProd == null || estProd <= 0) continue;
            if (!Number.isFinite(Number(systemKw)) || Number(systemKw) <= 0) continue;
            rows.push({
                cardKey,
                label: wishlistRowTitle(design, sub),
                snapshotUrl: resolveWishlistedDesignSnapshotUrl(
                    design,
                    layoutSnapshotsByKey,
                    aiCategoryLayoutVariants,
                    fallbackSnapshotUrl,
                    sub,
                    treeScenariosByLayoutKey,
                ),
                estProd,
                systemKw,
                multiplier: layoutMetricsByKey[design.layoutKey]?.multiplier ?? null,
            });
        }
        return rows;
    }, [scenarioCards, layoutSnapshotsByKey, aiCategoryLayoutVariants, fallbackSnapshotUrl, treeScenariosByLayoutKey, modulePowerW, layoutMetricsByKey]);

    const selectedWishlistCardKey = useMemo(() => {
        if (selectedDesignForPricing?.designId) return selectedDesignForPricing.designId;
        const active = scenarioCards.find(
            ({ design, sub }) =>
                design.aiDesignId === activeAIDesignId && sub.id === activeSubScenarioId,
        );
        if (!active) return null;
        return wishlistScenarioCardKey(active.design, active.sub);
    }, [
        selectedDesignForPricing?.designId,
        scenarioCards,
        activeAIDesignId,
        activeSubScenarioId,
    ]);

    return (
        <div className={`editor-ai-scenarios-block obd-ai-panel-surface obd-wishlist-section obd-theme-${theme}`}>
            {!hideHeaderChrome ? (
                <>
                    <div className="editor-ai-panel-title obd-wishlist-section__panel-title">
                        <HeartIcon size={14} weight="fill" aria-hidden />
                        Wishlist
                    </div>
                    <div className="obd-wishlist-section__scenario-count">
                        Wishlist Scenarios ({scenarioCards.length})
                    </div>
                </>
            ) : null}
            {!hasAi ? (
                <p className="obd-wishlist-section__empty">
                    No wishlisted AI designs yet. Click the heart on any AI card to add it.
                </p>
            ) : null}
            {hasAi ? (
                <div className="editor-ai-layout-options-scroll editor-ai-layout-options-scroll--card-mode">
                    <div className="editor-ai-layout-options-stack editor-ai-layout-options-stack--card-mode">
                        <div className="editor-ai-layout-card-scroll-body">
                            <div
                                className={`editor-ai-designs-grid editor-ai-layout-options-stack editor-ai-designs-grid--catalog${
                                    isDashboardOpen ? '' : ' obd-wishlist-catalog-grid--single-col'
                                }`}
                            >
                                <div className={`obd-ai-catalog-list obd-ai-catalog-list--dashboard obd-theme-${theme}`} role="list">
                                {scenarioCards.map(({ design, sub }, index) => {
                                        const isActive =
                                            activeAIDesignId === design.aiDesignId && activeSubScenarioId === sub.id;
                                        const trees = design.treeCount ?? wishlistTreeCount(sub, design);
                                        const summaryLine = `${sub.modules ?? design.modules ?? '—'} modules · ${sub.roofCount ?? design.roofCount ?? '—'} roofs · ${trees} trees`;
                                        const prodLine = formatDashboardAnnualProductionLine(
                                            wishlistScenarioProductionKwh(sub, design),
                                        );
                                        const detailLine = buildDashboardLayoutPerfDetailLine(
                                            layoutMetricsByKey[design.layoutKey],
                                        );
                                        const rowTitle = wishlistRowTitle(design, sub);
                                        const statusLabel = isActive ? 'Active' : 'Ready';
                                        const statusVariant: DashboardAiCatalogListRowStatusVariant = isActive
                                            ? 'lime'
                                            : 'neutral';
                                        const systemKwStr = (() => {
                                            const n = Number(wishlistScenarioSystemKw(sub, design, modulePowerW));
                                            return Number.isFinite(n) && n > 0 ? n.toFixed(2) : '—';
                                        })();
                                        const pricingBusy =
                                            pricingSelectionLoading &&
                                            (selectedDesignForPricing?.designId === design.aiDesignId ||
                                                pricingDesignForTrigger?.designId === design.aiDesignId);
                                        const aiCardKey = wishlistScenarioCardKey(design, sub);
                                        const epcBusy = isMainCardEpcLoading(aiCardKey);
                                        const pricingDesignPayload = (): SavedAIDesign => ({ ...design });
                                        return (
                                                <DashboardAiCatalogListRow
                                                    key={`${design.aiDesignId}-${sub.id}`}
                                                    theme={theme}
                                                    variant="dashboard"
                                                    rowIndex={index}
                                                    cardKey={aiCardKey}
                                                    snapshotUrl={resolveWishlistedDesignSnapshotUrl(
                                                        design,
                                                        layoutSnapshotsByKey,
                                                        aiCategoryLayoutVariants,
                                                        fallbackSnapshotUrl,
                                                        sub,
                                                        treeScenariosByLayoutKey,
                                                    )}
                                                    snapshotLoading={isLoadingSnapshots}
                                                    emptyThumb="sparkle"
                                                    showThumbIndex={false}
                                                    label={rowTitle}
                                                    summaryLine={summaryLine}
                                                    prodLine={prodLine}
                                                    detailLine={detailLine}
                                                    statusLabel={statusLabel}
                                                    statusVariant={statusVariant}
                                                    systemKw={systemKwStr}
                                                    isSelected={isActive}
                                                    isSimulating={false}
                                                    selectForPricingDisabled={
                                                        pricingSelectionLoading &&
                                                        (isActive || pricingDesignForTrigger?.designId === design.aiDesignId)
                                                    }
                                                    pricingBusy={pricingBusy}
                                                    onSelectForPricing={() => {
                                                        setActiveAIDesignId(design.aiDesignId);
                                                        setActiveSubScenarioId(sub.id);
                                                        const selectionKey =
                                                            design.layoutKey === 'default'
                                                                ? 'default'
                                                                : design.categoryId?.trim() || design.layoutKey;
                                                        setSelectedRoofCombinationKey(selectionKey);
                                                    }}
                                                    onActivateDesign={() => openWishlistRowInViewer(design, sub)}
                                                    onPricing={() => handlePricingForSavedDesign(pricingDesignPayload())}
                                                    showEpcAction={Boolean(solarUtilityData)}
                                                    epcBusy={epcBusy}
                                                    onOpenEpc={handleOpenEpcModal}
                                                    onOpen3D={() => openWishlistRowInViewer(design, sub)}
                                                    extraActions={
                                                        <button
                                                            type="button"
                                                            className="obd-ai-slide-designs__3d-btn obd-dashboard-section-rail__btn obd-ai-catalog-list__rail-btn obd-ai-catalog-list__icon-btn obd-ai-catalog-list__icon-btn--wishlist obd-ai-catalog-list__icon-btn--saved"
                                                            title="Remove from wishlist"
                                                            aria-label="Remove from wishlist"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                removeFromWishlist(design.aiDesignId, sub.id);
                                                            }}
                                                        >
                                                            <span
                                                                className="obd-dashboard-section-rail__icon-wrap obd-ai-catalog-list__rail-icon-wrap"
                                                                aria-hidden
                                                            >
                                                                <HeartIcon size={19} weight="fill" aria-hidden />
                                                            </span>
                                                        </button>
                                                    }
                                                />
                                        );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            ) : null}
            <DashboardAiDesignsEpcModal
                theme={theme}
                open={epcModalOpen}
                onClose={handleCloseEpcModal}
                designs={epcModalDesigns}
                ensurePricingForCard={ensureMainCardPricingForCard}
                getEpcByPartner={getDesignEpcByPartner}
                getPartnerCellState={getMainCardPartnerCellState}
                selectedCardKey={selectedWishlistCardKey}
            />
        </div>
    );
}

export default WishListSection;
